import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Bell, UserPlus, Package, Receipt, CheckCircle2, XCircle, BadgeCheck, CheckCheck, Flag } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { api } from "@/lib/api";
import { mapActionUrl, refreshUnread, adjustUnread } from "@/lib/notifications";
import { useLang } from "@/context/LanguageContext";
import { notifTitle } from "@/lib/notifText";
import { Loading, Empty, useAdminFetch } from "@/components/admin/shared";

const ICONS = { user_pending: UserPlus, ad_pending: Package, receipt: Receipt, receipt_admin: Receipt, account_status: BadgeCheck, report: Flag };

const iconFor = (n) => (n.type === "ad_status" ? (n.title?.includes("رفض") ? XCircle : CheckCircle2) : ICONS[n.type] || Bell);

const timeAgo = (iso, lang) => {
  const d = new Date(iso?.endsWith("Z") || iso?.includes("+") ? iso : `${iso}Z`);
  const mins = Math.max(1, Math.round((Date.now() - d) / 60000));
  const rtf = new Intl.RelativeTimeFormat(lang === "en" ? "en" : "ar-u-nu-latn", { numeric: "auto" });
  if (mins < 60) return rtf.format(-mins, "minute");
  if (mins < 1440) return rtf.format(-Math.round(mins / 60), "hour");
  return rtf.format(-Math.round(mins / 1440), "day");
};

export default function Notifications() {
  const navigate = useNavigate();
  const { lang } = useLang();
  const L = (ar, en) => (lang === "en" ? en : ar);
  const { data, loading, reload, setData } = useAdminFetch("/notifications");
  useEffect(() => {
    window.addEventListener("bazaro-new-notification", reload);
    return () => window.removeEventListener("bazaro-new-notification", reload);
  }, [reload]);
  const items = data || [];
  const unread = items.filter((n) => !n.read).length;
  const markRead = (pred, on) => {
    const n = items.filter((x) => pred(x) && !x.read).length;
    adjustUnread(on ? -n : n);
    setData((list) => (list || []).map((x) => (pred(x) ? { ...x, read: on } : x)));
  };

  const open = (n) => {
    if (!n.read) {
      markRead((x) => x.notif_id === n.notif_id, true);
      api.post(`/notifications/${n.notif_id}/read`).then(refreshUnread).catch(() => markRead((x) => x.notif_id === n.notif_id, false));
    }
    navigate(mapActionUrl(n.action_url, n.type));
  };
  const readAll = async () => {
    const ids = new Set(items.filter((n) => !n.read).map((n) => n.notif_id));
    markRead((x) => ids.has(x.notif_id), true);
    try { await api.post("/notifications/read-all"); refreshUnread(); }
    catch { markRead((x) => ids.has(x.notif_id), false); toast.error(L("تعذّر تحديث الإشعارات", "Couldn't update notifications")); }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navbar />
      <main className="max-w-3xl mx-auto px-5 w-full py-8 flex-1" data-testid="notifications-page">
        <div className="flex items-center justify-between mb-6">
          <h1 className="font-headline text-2xl font-bold">{L("الإشعارات", "Notifications")}</h1>
          {unread > 0 && (
            <button onClick={readAll} className="flex items-center gap-1.5 text-sm font-semibold text-[#1A5C3A] hover:underline" data-testid="notifications-read-all">
              <CheckCheck className="w-4 h-4" /> {L("تعليم الكل كمقروء", "Mark all as read")}
            </button>
          )}
        </div>
        {loading ? <Loading /> : !items.length ? <Empty testid="notifications-empty">{L("لا توجد إشعارات جديدة", "No notifications yet")}</Empty> : (
          <div className="space-y-2.5" data-testid="notifications-list">
            {items.map((n) => {
              const Icon = iconFor(n);
              return (
                <button key={n.notif_id} onClick={() => open(n)} data-testid={`notification-${n.notif_id}`}
                  className={`w-full text-start flex items-start gap-3 p-4 rounded-2xl border transition-colors ${n.read ? "bg-white border-slate-200 hover:bg-slate-50" : "bg-[#1A5C3A]/5 border-[#1A5C3A]/20 hover:bg-[#1A5C3A]/10"}`}>
                  <div className="w-11 h-11 rounded-full bg-[#E8EFEA] text-[#0F4D32] flex items-center justify-center shrink-0"><Icon className="w-5 h-5" /></div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm ${n.read ? "font-medium" : "font-bold"} text-slate-900`}>{(lang === "en" && n.title_en) || notifTitle(n.title, lang)}</p>
                    {n.message && <p className="text-sm text-slate-600 mt-0.5 truncate">{(lang === "en" && n.message_en) || n.message}</p>}
                    <p className="text-xs text-slate-400 mt-1">{timeAgo(n.created_at, lang)}</p>
                  </div>
                  {!n.read && <span className="w-2.5 h-2.5 rounded-full bg-red-600 mt-2 shrink-0" data-testid={`notification-unread-${n.notif_id}`} />}
                </button>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
